<template>
  <div class="game-history">
    <h3 class="history-title">Spielverlauf</h3>
    <div class="history-list">
      <div v-if="history.length === 0" class="empty-history">
        Der Spielverlauf wird hier angezeigt.
      </div>
      <div
        v-for="(item, index) in limitedHistory"
        :key="index"
        class="history-item"
        :class="{ 'is-current': index === 0 }"
      >
        <div class="history-top">
          <div class="history-player" v-if="shouldShowPlayer(item.task.type)">
            <i
              :class="
                item.player.gender === 'male'
                  ? 'fas fa-user'
                  : 'fas fa-user-alt'
              "
            ></i>
            {{ item.player.name }}
          </div>
          <div class="history-round">Runde {{ item.round }}</div>
        </div>
        <div class="history-task">
          <div class="history-task-type" :class="item.task.type">
            <i :class="getTaskIcon(item.task.type)"></i>
          </div>
          <div class="history-task-text">{{ shortenText(item.task.text) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GameHistory",
  props: {
    history: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: -1,
    },
    maxEntries: {
      type: Number,
      default: 5,
    },
  },
  computed: {
    limitedHistory() {
      // Nur die letzten X Einträge zeigen (neueste zuerst)
      return [...this.history].reverse().slice(0, this.maxEntries);
    },
  },
  data() {
    return {
      taskTypes: {
        single: { icon: "fas fa-user" },
        group: { icon: "fas fa-users" },
        virus: { icon: "fas fa-virus" },
        rule: { icon: "fas fa-gavel" },
        truth: { icon: "fas fa-comment-dots" },
        game: { icon: "fas fa-gamepad" },
        challenge: { icon: "fas fa-flag-checkered" },
        rhyme: { icon: "fas fa-music" },
        category: { icon: "fas fa-list" },
        timed: { icon: "fas fa-stopwatch" },
        quiz: { icon: "fas fa-question" },
      },
    };
  },
  methods: {
    getTaskIcon(type) {
      return this.taskTypes[type]?.icon || "fas fa-question";
    },
    shortenText(text) {
      // Text auf maximal 60 Zeichen kürzen
      return text.length > 60 ? text.substring(0, 57) + "..." : text;
    },
    shouldShowPlayer(taskType) {
      // Aufgabentypen, die keinen Spielernamen anzeigen
      const noPlayerTypes = ["group", "rule"];
      return !noPlayerTypes.includes(taskType);
    },
  },
};
</script>

<style scoped>
.game-history {
  background-color: var(--secondary-bg);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  font-size: var(--font-size-xs);
}

.history-title {
  padding: var(--spacing-xs);
  border-bottom: 1px solid var(--card-bg);
  font-size: var(--font-size-sm);
  text-align: center;
}

.history-list {
  max-height: 30vh;
  overflow-y: auto;
  padding: var(--spacing-xs);
}

.empty-history {
  text-align: center;
  padding: var(--spacing-xs);
  color: #8a8a8a;
  font-size: var(--font-size-xs);
}

.history-item {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xs);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color var(--transition-fast);
}

.history-item:last-child {
  border-bottom: none;
}

.history-item.is-current {
  background-color: rgba(0, 224, 208, 0.05);
}

.history-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.history-player {
  font-size: var(--font-size-xs);
  font-weight: 600;
  display: flex;
  align-items: center;
}

.history-player i {
  margin-right: 4px;
  color: var(--accent-color);
}

.history-round {
  font-size: 0.7rem;
  background-color: var(--card-bg);
  padding: 2px 5px;
  border-radius: 10px;
  color: #8a8a8a;
}

.history-task {
  display: flex;
  align-items: flex-start;
  font-size: var(--font-size-xs);
}

.history-task-type {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
}

.history-task-type i {
  color: var(--bg-color);
  font-size: 10px;
}

.history-task-type.single {
  background-color: #00c2ff;
}

.history-task-type.group {
  background-color: #4caf50;
}

.history-task-type.virus {
  background-color: #ff0000;
}

.history-task-type.rule {
  background-color: #ffcc00;
}

.history-task-type.truth {
  background-color: #b942ff;
}

.history-task-type.game {
  background-color: #ff5c8d;
}

.history-task-type.challenge {
  background-color: #ff9500;
}

.history-task-type.rhyme {
  background-color: #2196f3;
}

.history-task-type.category {
  background-color: #8bc34a;
}

.history-task-type.timed {
  background-color: #f44336;
}

.history-task-type.quiz {
  background-color: #9c27b0;
}

.history-task-text {
  line-height: 1.3;
  flex: 1;
}
</style>
